import {
  SOCCER_TRADING_COMPETITION_CODE,
  isSoccerTradingCompetition,
} from './competitionIdentity';

export const WORLD_CUP_COMPETITION_CODE = SOCCER_TRADING_COMPETITION_CODE;
export const WORLD_CUP_ALL_GROUPS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
] as const;

export type WorldCupGroup = (typeof WORLD_CUP_ALL_GROUPS)[number];
export type WorldCupGroupFilterValue = 'ALL' | WorldCupGroup;
export type WorldCupStatusFilterValue = 'ALL' | 'LIVE' | 'ELIMINATED';

export type WorldCupFilterableTeam = {
  team_code: string;
  team_name?: string | null;
  team_short_name?: string | null;
  group?: string | null;
  team_state?: string | null;
};

export type WorldCupTeamFilterState = {
  selectedGroup: WorldCupGroupFilterValue;
  searchQuery: string;
  statusFilter?: WorldCupStatusFilterValue;
};

export type WorldCupTeamSection<TTeam> = {
  title: string;
  group: WorldCupGroup | 'OTHER' | 'ELIMINATED';
  data: TTeam[];
};

export const WORLD_CUP_GROUP_BY_TEAM_CODE: Record<string, WorldCupGroup> = {
  MEX: 'A',
  CZE: 'A',
  RSA: 'A',
  KOR: 'A',
  CAN: 'B',
  BIH: 'B',
  QAT: 'B',
  SUI: 'B',
  BRA: 'C',
  HAI: 'C',
  MAR: 'C',
  SCO: 'C',
  USA: 'D',
  AUS: 'D',
  PAR: 'D',
  TUR: 'D',
  CUW: 'E',
  ECU: 'E',
  GER: 'E',
  CIV: 'E',
  NED: 'F',
  JPN: 'F',
  SWE: 'F',
  TUN: 'F',
  BEL: 'G',
  EGY: 'G',
  IRN: 'G',
  NZL: 'G',
  CPV: 'H',
  KSA: 'H',
  ESP: 'H',
  URU: 'H',
  FRA: 'I',
  NOR: 'I',
  SEN: 'I',
  IRQ: 'I',
  ALG: 'J',
  ARG: 'J',
  AUT: 'J',
  JOR: 'J',
  COL: 'K',
  COD: 'K',
  POR: 'K',
  UZB: 'K',
  CRO: 'L',
  ENG: 'L',
  GHA: 'L',
  PAN: 'L',
};

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function isEliminatedTeam(team: Pick<WorldCupFilterableTeam, 'team_state'>): boolean {
  return normalizeText(team.team_state) === 'ELIMINATED';
}

export function isWorldCupCompetition(competitionCode: string | null | undefined) {
  return isSoccerTradingCompetition(competitionCode);
}

export function resolveWorldCupTeamGroup(
  team: Pick<WorldCupFilterableTeam, 'team_code' | 'group'>,
): WorldCupGroup | null {
  const explicitGroup = normalizeText(team.group);
  if ((WORLD_CUP_ALL_GROUPS as readonly string[]).includes(explicitGroup)) {
    return explicitGroup as WorldCupGroup;
  }
  return WORLD_CUP_GROUP_BY_TEAM_CODE[normalizeText(team.team_code)] ?? null;
}

export function filterWorldCupTeams<TTeam extends WorldCupFilterableTeam>(
  teams: TTeam[],
  filterState: WorldCupTeamFilterState,
): TTeam[] {
  const selectedGroup = filterState.selectedGroup;
  const statusFilter = filterState.statusFilter ?? 'ALL';
  const query = normalizeText(filterState.searchQuery);

  return teams.filter((team) => {
    const teamGroup = resolveWorldCupTeamGroup(team);
    if (selectedGroup !== 'ALL' && teamGroup !== selectedGroup) {
      return false;
    }
    if (statusFilter === 'LIVE' && isEliminatedTeam(team)) {
      return false;
    }
    if (statusFilter === 'ELIMINATED' && !isEliminatedTeam(team)) {
      return false;
    }
    if (!query) {
      return true;
    }

    const searchable = [team.team_code, team.team_name, team.team_short_name]
      .map(normalizeText)
      .join(' ');
    return searchable.includes(query);
  });
}

export function buildWorldCupTeamSections<TTeam extends WorldCupFilterableTeam>(
  teams: TTeam[],
  filterState: WorldCupTeamFilterState,
): WorldCupTeamSection<TTeam>[] {
  const filteredTeams = filterWorldCupTeams(teams, filterState);
  const activeTeams = filteredTeams.filter((team) => !isEliminatedTeam(team));
  const eliminatedTeams = filteredTeams.filter(isEliminatedTeam);
  const teamsByGroup = new Map<WorldCupGroup | 'OTHER', TTeam[]>();

  activeTeams.forEach((team) => {
    const group = resolveWorldCupTeamGroup(team) ?? 'OTHER';
    const existing = teamsByGroup.get(group) ?? [];
    existing.push(team);
    teamsByGroup.set(group, existing);
  });

  const orderedGroups: Array<WorldCupGroup | 'OTHER'> =
    filterState.selectedGroup === 'ALL'
      ? [...WORLD_CUP_ALL_GROUPS, 'OTHER']
      : [filterState.selectedGroup];

  const activeSections = orderedGroups
    .map((group) => {
      const data = teamsByGroup.get(group) ?? [];
      return {
        title: group === 'OTHER' ? 'Other Teams' : `Group ${group}`,
        group,
        data,
      };
    })
    .filter((section) => section.data.length > 0);

  if (eliminatedTeams.length === 0) {
    return activeSections;
  }

  return [
    ...activeSections,
    {
      title: 'Eliminated Teams',
      group: 'ELIMINATED',
      data: eliminatedTeams,
    },
  ];
}
